import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    Search,
    Save,
    CheckCircle2,
    ChevronDown,
    X,
    Mail,
    Send,
    SquarePen,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { ListItem } from "@mui/material";
import MultiSelectWithSearchInput from "../../components/infiniteMultiSelect/withInfiniteMultiSelect";
import styles from "../../../Samarth Module/pages/Alerts/alert.module.css";
import resetBtn from "../../../assets/images/resetBtn.png";
import MoreFilters from "../emailTriaging/MoreFilters";
import ColumnSortIcon from "Agentic Module/components/common/columnSortIcon";
import "./hitl.css";
import { ALL_FILTERS, EMPTY_SELECTED_FILTERS, FILTER_BY_LABEL, UOM_OPTIONS, VISIBLE_FILTER_LIMIT } from "./hitlConstant";
import { emailHITLGetEmailHistory, hitlEmailTableData, hitlGetFilters, hitlGetReferenceData, saveHitlRows } from "./hitlAPI";
import { loadPersistedSelectedFilters, savePersistedSelectedFilters, clearPersistedSelectedFilters, normalizeDispatchStatusFilterApiValues } from "../emailTriaging/emailTriageUtils";
import { useMsal } from "@azure/msal-react";
import { normalizeDispatchRow } from "../emailTriaging/emailTriageApi";
import { PaginationSkeleton, TableBodySkeletonRows } from "Agentic Module/components/skeletonLoader/AgenticPageSkeleton";
import DraftHITLEmail from "../hitl/draftHITLEmail";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import { toast, Toaster } from "sonner";
import PropTypes from "prop-types";


function buildDefaultRmpmFilters(selected = EMPTY_SELECTED_FILTERS) {
    return ALL_FILTERS.map((f) => ({
        label: f.label,
        selectedData: Array.isArray(selected[f.key]) ? [...selected[f.key]] : [],
    }));
}

const HITL_SELECTED_FILTERS_STORAGE_KEY = "scnc_hitl_selected_filters";

function normalizeHitlSelectedFilters(filters) {
    const source = filters && typeof filters === "object" ? filters : {};
    return ALL_FILTERS.reduce((acc, filter) => {
        acc[filter.key] = Array.isArray(source[filter.key]) ? source[filter.key] : [];
        return acc;
    }, {});
}

function readPersistedHitlFilters() {
    return normalizeHitlSelectedFilters(
        loadPersistedSelectedFilters(
            HITL_SELECTED_FILTERS_STORAGE_KEY,
            EMPTY_SELECTED_FILTERS,
        ),
    );
}


const INTENT_CLASS = {
    transporter: "ehl-intent--transporter",
    "supplier inventory": "ehl-intent--inventory",
    "dispatch data": "ehl-intent--dispatch",
    factory: "ehl-intent--factory",
};

const MAIL_KIND_ICON = {
    inbox: <ChevronDown size={10} className="ehl-rotate-270" />,
    sent: <Send size={9} />,
    draft: <Save size={9} />,
};

const HITL_REFERENCE_OPTION_KEYS = {
    siteCode: "site_codes",
    siteName: "site_names",
    vendorCode: "vendor_codes",
    vendorName: "vendor_names",
    materialCode: "material_codes",
    materialName: "material_names",
};

const HITL_TABLE_COLUMNS = [
    { key: "email_intent", label: "Email Intent", sortKey: "email_intent" },
    { key: "email_subject", label: "Subject", sortKey: "email_subject" },
    { key: "from", label: "From", sortKey: "from" },
    { key: "to", label: "To", sortKey: "to" },
    { key: "vendor", label: "Vendor", sortKey: "vendor" },
    { key: "site", label: "Site", sortKey: "site" },
    { key: "material_group", label: "Material Group", sortKey: "material_group" },
    { key: "hitl_reasons", label: "HITL Reason", sortKey: "hitl_reason" },
    { key: "status", label: "Status", sortKey: "status" },
    { key: "_email", label: "Email", sortable: false, center: true },
    { key: "_action", label: "Action", sortable: false, center: true },
];

const HITL_FIELD_ALIASES = {
    vendor: ["supplier_name", "vendor", "vendor_name", "vendorName", "supplierName"],
    vendor_code: ["vendor_code", "vendorCode", "supplier_code", "supplierCode"],
    site: ["unit_name", "site", "site_name", "site_cluster", "siteCluster", "siteName"],
    site_code: ["site_code", "siteCode", "site_unit_code", "unit_code"],
    material: ["mat_name", "material", "material_name", "material_group", "material_desc", "materialDescription"],
    material_code: ["mat_code", "material_code", "materialCode", "item_code"],
    po_no: ["po_no", "poNumber", "po_number"],
    invoice_no: ["invoice_no", "invoiceNo", "invoice_number"],
    vehicle_no: ["vehicle_no", "vehicleNo", "vehicle_number"],
    dispatch_qty: ["dispatch_qty", "quantity", "qty"],
    stock_qty: ["dispatch_qty", "stock_qty", "stockQty", "quantity", "qty"],
    uom: ["uom", "unit", "UOM"],
    date_of_dispatch: ["date_of_dispatch", "dispatchDate", "dispatch_date"],
};

const HITL_EDIT_FIELD_TO_PAYLOAD_FIELD = {
    vendor: "supplier_name",
    vendor_code: "vendor_code",
    site: "unit_name",
    site_code: "site_code",
    material: "mat_name",
    material_code: "mat_code",
    stock_qty: "dispatch_qty",
    dispatch_qty: "dispatch_qty",
    po_no: "po_no",
    invoice_no: "invoice_no",
    vehicle_no: "vehicle_no",
    uom: "uom",
    date_of_dispatch: "date_of_dispatch",
};

function normalizeUomValue(value) {
    const normalizedValue = String(value ?? "").trim();
    if (!normalizedValue) return "";

    return UOM_OPTIONS.find((option) => option.toLowerCase() === normalizedValue.toLowerCase()) ?? normalizedValue;
}

function normalizeIntentKey(intent) {
    return String(intent ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function getReferenceValue(referenceData, keys) {
    const value = (Array.isArray(keys) ? keys : [keys])
        .map((key) => referenceData?.[key])
        .find((candidate) => candidate !== undefined && candidate !== null);
    return Array.isArray(value) ? String(value[0] ?? "").trim() : String(value ?? "").trim();
}

function getHitlEditColumnsByIntent(intent) {
    const intentKey = normalizeIntentKey(intent);
    const baseDispatchColumns = [
        { key: "vendor", label: "Vendor", type: "master", fieldType: "vendorName" },
        { key: "vendor_code", label: "Vendor Code", type: "master", fieldType: "vendorCode" },
        { key: "site", label: "Site", type: "master", fieldType: "siteName" },
        { key: "site_code", label: "Site Code", type: "master", fieldType: "siteCode" },
        { key: "material_code", label: "Material Code", type: "master", fieldType: "materialCode" },
        { key: "date_of_dispatch", label: "Dispatch Date", type: "date" },
        { key: "po_no", label: "PO Number", type: "text", placeholder: "po number", fieldType: "poNumber" },
        { key: "invoice_no", label: "Invoice No", type: "text", placeholder: "invoice number" },
        { key: "vehicle_no", label: "Vehicle No", type: "text", placeholder: "vehicle number" },
        { key: "dispatch_qty", label: "Quantity", type: "number", placeholder: "0" },
        { key: "uom", label: "UOM", type: "text", placeholder: "UOM" },
    ];

    if (intentKey === "supplier_inventory" || intentKey === "inventory") {
        return [
            { key: "vendor", label: "Vendor", type: "master", fieldType: "vendorName" },
            { key: "vendor_code", label: "Vendor Code", type: "master", fieldType: "vendorCode" },
            { key: "site", label: "Site", type: "master", fieldType: "siteName" },
            { key: "site_code", label: "Site Code", type: "master", fieldType: "siteCode" },
            { key: "material_code", label: "Material Code", type: "master", fieldType: "materialCode" },
            { key: "stock_qty", label: "Stock Qty", type: "number", placeholder: "0" },
            { key: "uom", label: "UOM", type: "text", placeholder: "UOM" },
        ];
    }

    if (intentKey === "dispatch_data" || intentKey === "dispatch") {
        return baseDispatchColumns;
    }

    if (intentKey === "transporter") {
        return [
            { key: "vendor", label: "Vendor", type: "master", fieldType: "vendorName" },
            { key: "vendor_code", label: "Vendor Code", type: "master", fieldType: "vendorCode" },
            { key: "site", label: "Site", type: "master", fieldType: "siteName" },
            { key: "site_code", label: "Site Code", type: "master", fieldType: "siteCode" },
            { key: "material_code", label: "Material Code", type: "master", fieldType: "materialCode" },
            { key: "po_no", label: "PO Number", type: "text", placeholder: "po number" },
            { key: "invoice_no", label: "Invoice No", type: "text", placeholder: "invoice number" },
            { key: "vehicle_no", label: "Vehicle No", type: "text", placeholder: "vehicle number" },
            { key: "dispatch_qty", label: "Quantity", type: "number", placeholder: "0" },
            { key: "uom", label: "UOM", type: "text", placeholder: "UOM" },
        ];
    }

    return baseDispatchColumns;
}

function StatusBadge({ status }) {
    const map = {
        pending: { label: "Pending", cls: "ehl-status--pending" },
        validated: { label: "Validated", cls: "ehl-status--validated" },
    };
    const { label, cls } = map[status] ?? map.pending;
    return <span className={`ehl-status ${cls}`}>{label}</span>;
}

StatusBadge.propTypes = {
    status: PropTypes.string,
};

export function HitlBadge({ reasons }) {
    if (!reasons || reasons.length === 0) return <span className="ehl-hitl-dash">—</span>;
    return <span className="ehl-hitl-list">{reasons.join(", ")}</span>;
}

HitlBadge.propTypes = {
    reasons: PropTypes.arrayOf(PropTypes.string),
};

function ReadOnlyField({ label, value, wide }) {
    return (
        <div className={wide ? "ehl-ro-field--wide" : ""}>
            <label className="ehl-ro-label">{label}</label>
            <div className={`ehl-ro-value ${wide ? "ehl-ro-value--wide" : ""}`}>
                {value || <span className="ehl-ro-empty">—</span>}
            </div>
        </div>
    );
}

ReadOnlyField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node,
    wide: PropTypes.bool,
};

function HitlValidationResultModal({ result, onClose }) {
    if (!result) {
        return null;
    }

    const statusClassName = result.success
        ? "hitl-result-modal-card--success"
        : "hitl-result-modal-card--error";

    const title = result.success
        ? "Validation Successful"
        : "Validation Failed";

    const StatusIcon = result.success ? CheckCircle2 : X;

    return (
        <div className="hitl-result-modal">
            <button
                type="button"
                className="hitl-result-modal-backdrop"
                onClick={onClose}
                aria-label="Close validation result dialog"
            />

            <dialog
                open
                className={`hitl-result-modal-card ${statusClassName}`}
                aria-labelledby="hitl-result-title"
                aria-describedby="hitl-result-description"
                onCancel={onClose}
            >
                <StatusIcon size={28} aria-hidden="true" />

                <h3 id="hitl-result-title">
                    {title}
                </h3>

                <p id="hitl-result-description">
                    {result.message}
                </p>

                <button
                    type="button"
                    className="ehl-btn-primary"
                    onClick={onClose}
                >
                    Close
                </button>
            </dialog>
        </div>
    );
}

HitlValidationResultModal.propTypes = {
    result: PropTypes.shape({
        success: PropTypes.bool,
        message: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

function HitlSaveConfirmationModal({ open, onCancel, onConfirm }) {
    if (!open) {
        return null;
    }

    return (
        <div className="hitl-result-modal">
            <button
                type="button"
                className="hitl-result-modal-backdrop"
                onClick={onCancel}
                aria-label="Close confirmation dialog"
            />

            <dialog
                open
                className="hitl-result-modal-card hitl-result-modal-card--confirmation"
                aria-labelledby="hitl-save-confirmation-title"
                aria-describedby="hitl-save-confirmation-description"
                onCancel={onCancel}
            >
                <Save size={28} aria-hidden="true" />

                <h3 id="hitl-save-confirmation-title">
                    Confirm Validation
                </h3>

                <p id="hitl-save-confirmation-description">
                    Are you sure you want to validate and save the selected line items?
                </p>

                <div className="hitl-confirmation-actions">
                    <button
                        type="button"
                        className="ehl-btn-ghost"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="ehl-btn-primary"
                        onClick={onConfirm}
                    >
                        Confirm
                    </button>
                </div>
            </dialog>
        </div>
    );
}

HitlSaveConfirmationModal.propTypes = {
open: PropTypes.bool.isRequired,
onCancel: PropTypes.func.isRequired,
onConfirm: PropTypes.func.isRequired,
};

function HitlMasterDropdown({
    value,
    fieldType,
    placeholder,
    onChange,
    className = "",
    isDisabled,
    poNumber,
    referenceParams = {},
    onMissingPo,
}) {
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const rootRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const requestSeqRef = useRef(0);
    const [menuPosition, setMenuPosition] = useState(null);
    const displayValue = String(value ?? "").trim();
    const normalizedPoNumber = String(poNumber ?? "").trim();
    const hasValidPoNumber = /^\d{10}$/.test(normalizedPoNumber);
    const { site_code = "", vendor_code = "", material_code = "" } = referenceParams;

    const fetchReferenceData = useCallback(async (requestId, selectedCode = "") => {
        setLoading(true);
        try {
            const raw = await hitlGetReferenceData({
                po_number: normalizedPoNumber,
                site_code: fieldType === "siteCode" ? selectedCode || site_code : site_code,
                vendor_code: fieldType === "vendorCode" ? selectedCode || vendor_code : vendor_code,
                material_code: fieldType === "materialCode" ? selectedCode || material_code : material_code,
            });
            if (requestId !== requestSeqRef.current) return;
            const optionKey = HITL_REFERENCE_OPTION_KEYS[fieldType];
            const values = Array.isArray(raw?.[optionKey]) ? raw[optionKey] : [];
            setOptions(values.map((option) => {
                const value = String(option ?? "").trim();
                return { code: value, name: value };
            }).filter((option) => option.code));
        } catch (error) {
            console.error("Failed to fetch reference data:", error);
            const responseMessage = error?.responseBody?.message ??
                error?.responseBody?.detail ??
                error?.responseBody?.error ??
                error?.responseBody?.errors;
            const message = Array.isArray(responseMessage)
                ? responseMessage.join(", ")
                : String(responseMessage ?? error?.message ?? "Failed to fetch reference data.");
            toast.error(message);
            if (requestId === requestSeqRef.current) setOptions([]);
        } finally {
            if (requestId === requestSeqRef.current) setLoading(false);
        }
    }, [fieldType, material_code, normalizedPoNumber, site_code, vendor_code]);

    useEffect(() => {
        if (!open || !hasValidPoNumber) return undefined;
        const requestId = requestSeqRef.current + 1;
        requestSeqRef.current = requestId;
        const timer = setTimeout(() => fetchReferenceData(requestId), 0);

        return () => clearTimeout(timer);
    }, [fetchReferenceData, hasValidPoNumber, open]);

    useEffect(() => {
        if (!open) return undefined;
        const updateMenuPosition = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            setMenuPosition({
                left: rect.left,
                top: rect.bottom + 6,
                width: rect.width,
            });
        };

        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);
        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event) => {
            const clickedTrigger = rootRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);
            if (!clickedTrigger && !clickedMenu) {
                setOpen(false);
                setSearchText("");
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    const normalizedOptions = useMemo(() => {
        const seen = new Set();
        const list = [];
        if (displayValue) {
            seen.add(displayValue.toLowerCase());
            list.push({ code: displayValue, name: displayValue });
        }
        options.forEach((option) => {
            const label = String(option?.name || option?.code || "").trim();
            if (!label) return;
            const key = label.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            list.push({ code: String(option?.code ?? label), name: label });
        });
        const query = searchText.trim().toLowerCase();
        return query
            ? list.filter((option) =>
                option.code.toLowerCase().includes(query) || option.name.toLowerCase().includes(query),
            )
            : list;
    }, [displayValue, options, searchText]);

    const renderDropdownContent = () => {
        let content;

        if (loading && normalizedOptions.length === 0) {
            content = (
                <li className="hitl-master-dropdown-empty">
                    Loading...
                </li>
            );
        } else if (normalizedOptions.length === 0) {
            content = (
                <li className="hitl-master-dropdown-empty">
                    No matches
                </li>
            );
        } else {
            content = normalizedOptions.map((option) => {
                const optionValue = fieldType.endsWith("Code")
                    ? option.code
                    : option.name || option.code;

                const selected = optionValue === displayValue;

                const handleOptionClick = () => {
                    onChange(optionValue);

                    if (fieldType.endsWith("Code") && hasValidPoNumber) {
                        const requestId = requestSeqRef.current + 1;
                        requestSeqRef.current = requestId;
                        fetchReferenceData(requestId, optionValue);
                    }

                    setOpen(false);
                    setSearchText("");
                };

                return (
                    <li key={`${option.code}-${option.name}`}>
                        <button
                            type="button"
                            aria-pressed={selected}
                            className={[
                                "hitl-master-dropdown-option",
                                selected && "hitl-master-dropdown-option--selected",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={handleOptionClick}
                            title={optionValue}
                            disabled={isDisabled}
                        >
                            {optionValue}
                        </button>
                    </li>
                );
            });
        }

        return <>{content}</>;
    };

    return (
        <div className={`hitl-master-dropdown ${className}`.trim()} ref={rootRef} disabled={isDisabled}>
            <button
                type="button"
                className="hitl-master-dropdown-trigger"
                ref={triggerRef}
                onClick={() => {
                    if (!hasValidPoNumber) {
                        onMissingPo?.();
                        return;
                    }
                    setOpen((prev) => !prev);
                }}
                aria-expanded={open}
                aria-haspopup="listbox"
                disabled={isDisabled}
            >
                <span className={displayValue ? "hitl-master-dropdown-value" : "hitl-master-dropdown-placeholder"}>
                    {displayValue || placeholder}
                </span>
                <ChevronDown size={14} className="hitl-master-dropdown-chevron" />
            </button>

            {open && menuPosition ? createPortal(
                <div
                    ref={menuRef}
                    className="hitl-master-dropdown-menu"
                    style={{
                        left: `${menuPosition.left}px`,
                        top: `${menuPosition.top}px`,
                        width: `${menuPosition.width}px`,
                    }}

                >
                    <div className="hitl-master-dropdown-search-wrap">
                        <Search size={14} className="hitl-master-dropdown-search-icon" />
                        <input
                            type="text"
                            className="hitl-master-dropdown-search"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            disabled={isDisabled}
                        />
                    </div>
                    <ul className="hitl-master-dropdown-list">
                        {renderDropdownContent()}
                    </ul>
                </div>,
                document.body,
            ) : null}
        </div>
    );
}

HitlMasterDropdown.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    fieldType: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    isDisabled: PropTypes.bool,
    poNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    referenceParams: PropTypes.shape({
        site_code: PropTypes.string,
        vendor_code: PropTypes.string,
        material_code: PropTypes.string,
    }),
    onMissingPo: PropTypes.func,
};

function Cell({ children, className = "" }) {
    return <td className={`ehl-td ${className}`}>{children}</td>;
}

Cell.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};

function resolveLineFieldValue(row, field, record = null) {
    const directValueFromRow = (() => {
        const candidates = [field, ...(HITL_FIELD_ALIASES[field] || [])];
        for (const key of candidates) {
            if (row && key in row && row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== "") {
                return row[key];
            }
        }
        return null;
    })();

    if (directValueFromRow !== null) return directValueFromRow;
    const fieldMap = {
        vendor: "vendor",
        vendor_code: "vendor_code",
        site: "site",
        site_code: "site_code",
        material: "mat_name",
        material_code: "mat_code",
        vehicle_no: "vehicle_no",
        uom: "uom",
    };

    const mappedField = fieldMap[field];

    if (mappedField) {
        return record?.[mappedField] ?? "";
    }

    return row?.[field] ?? "";

}

function getLineRowKey(row, fallbackIndex) {
    if (row && (row.id || row.line_id || row.row_id || row.dispatch_row_id)) {
        return row.id ?? row.line_id ?? row.row_id ?? row.dispatch_row_id;
    }
    const fallback = [
        row?.vendor_name,
        row?.site_cluster,
        row?.material_code,
        row?.invoice_no,
        row?.vehicle_no,
        row?.po_no,
        row?.po_number,
        row?.material,
        row?.site,
        row?.vendor,
        fallbackIndex,
    ]
        .filter((value) => value !== undefined && value !== null && value !== "")
        .map(String)
        .join("-");
    return fallback || `line-${fallbackIndex}`;
}

function normalizeExpandLineItem(record, row, index) {
    const vendorValue = record?.vendor ?? record?.vendor_name ?? row?.vendor_name ?? row?.vendorName ?? row?.vendor ?? "";
    const vendorCodeValue = row?.vendor_code ?? row?.vendorCode ?? row?.supplier_code ?? record?.vendor_code ?? record?.vendorCode ?? "";
    const siteValue = record?.site ?? record?.site_name ?? record?.site_cluster ?? row?.site_name ?? row?.site_cluster ?? row?.siteCluster ?? row?.site ?? "";
    const siteCodeValue = row?.site_code ?? row?.siteCode ?? row?.unit_code ?? record?.site_code ?? record?.siteCode ?? "";
    const materialNameValue = row?.mat_name ?? row?.material_name ?? row?.material ?? record?.mat_name ?? record?.material_name ?? record?.material ?? record?.material_group ?? "";
    const materialCodeValue = row?.mat_code ?? row?.material_code ?? row?.materialCode ?? row?.item_code ?? record?.mat_code ?? record?.material_code ?? record?.materialCode ?? "";
    const vehicleValue = row?.vehicle_no ?? row?.vehicleNo ?? row?.vehicle_number ?? "";
    const uomValue = normalizeUomValue(row?.uom ?? row?.unit ?? row?.UOM ?? "");

    const normalized = {
        ...row,
        email_message_id: row?.email_message_id ?? row?.message_id ?? row?.mail_id ?? record?.email_message_id ?? record?.message_id ?? record?.mail_id ?? record?.id ?? null,
        vendor: vendorValue,
        vendor_name: vendorValue,
        vendor_code: vendorCodeValue,
        vendorCode: vendorCodeValue,
        site: siteValue,
        site_name: siteValue,
        site_cluster: siteValue,
        site_code: siteCodeValue,
        siteCode: siteCodeValue,
        material: materialNameValue,
        mat_name: materialNameValue,
        mat_code: materialCodeValue,
        material_code: materialCodeValue,
        materialCode: materialCodeValue,
        po_no: row?.po_no ?? row?.poNumber ?? row?.po_number ?? "",
        invoice_no: row?.invoice_no ?? row?.invoiceNo ?? row?.invoice_number ?? "",
        vehicle_no: vehicleValue,
        vehicleNo: vehicleValue,
        uom: uomValue,
        date_of_dispatch: row?.date_of_dispatch ?? row?.dispatchDate ?? row?.dispatch_date ?? "",
        dispatch_qty: row?.dispatch_qty ?? row?.quantity ?? row?.qty ?? "",
        stock_qty: row?.stock_qty ?? row?.stockQty ?? row?.quantity ?? row?.qty ?? "",
        id: row?.id ?? row?.line_id ?? row?.row_id ?? row?.dispatch_row_id ?? `line-${index}`,
    };
    return {
        ...normalized,
        __original: { ...normalized },
        __originalRaw: { ...row },
    };
}

function getRecordExpansionKey(record) {
    return record?.email_message_id ?? record?.id ?? record?.message_id ?? record?.mail_id ?? null;
}

function isFieldFlagged(record, field, row = null) {
    const reasons = [
        ...(Array.isArray(record?.hitl_reasons) ? record.hitl_reasons : []),
        ...(Array.isArray(record?.hitlReasons) ? record.hitlReasons : []),
        ...(Array.isArray(row?.hitl_reasons) ? row.hitl_reasons : []),
        ...(Array.isArray(row?.hitlReasons) ? row.hitlReasons : []),
    ];
    const text = reasons
        .map((reason) => String(reason ?? ""))
        .join(" ")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/[^a-z0-9\s]+/g, " ");
    const fieldKeywords = {
        vendor: ["vendor"],
        vendor_code: ["vendor code", "vendor"],
        site: ["site"],
        site_code: ["site code", "site"],
        material: ["material"],
        material_code: ["material code", "mat code", "material"],
        mat_code: ["material code", "mat code"],
        po_no: ["po", "po number", "po no", "purchase order", "purchase order number"],
        invoice_no: ["invoice"],
        vehicle_no: ["vehicle"],
        dispatch_qty: ["quantity", "qty"],
        stock_qty: ["stock", "quantity", "qty"],
        date_of_dispatch: ["dispatch date", "date"],
        uom: ["uom", "unit"],
    };
    return (fieldKeywords[field] || []).some((keyword) => {
        const normalizedKeyword = keyword.toLowerCase();
        return normalizedKeyword.includes(" ")
            ? text.includes(normalizedKeyword)
            : new RegExp(String.raw`\b${normalizedKeyword}\b`).test(text);
    });
}

function isPoNumberReason(record, row = null) {
    const reasons = [
        ...(Array.isArray(record?.hitl_reasons) ? record.hitl_reasons : []),
        ...(Array.isArray(record?.hitlReasons) ? record.hitlReasons : []),
        ...(Array.isArray(row?.hitl_reasons) ? row.hitl_reasons : []),
        ...(Array.isArray(row?.hitlReasons) ? row.hitlReasons : []),
    ];
    return reasons.some((reason) => /\b(po|po number|po no|purchase order|purchase order number)\b/i.test(String(reason ?? "").replace(/[_-]+/g, " ")));
}

export function EmailValidationTab({ alertType }) {
    const { accounts } = useMsal();
    const activeAccount = accounts[0] ?? null;
    const [expandedId, setExpandedId] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [selectedLines, setSelectedLines] = useState(new Set());
    const [savedId, setSavedId] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
    const [emailCenterRecord, setEmailCenterRecord] = useState(null);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailListTab, setEmailListTab] = useState("inbox");
    const [search, setSearch] = useState("");
    const [filterOptions, setFilterOptions] = useState({
        hitl_reason: [],
        intent: [],
        material: [],
        site: [],
        vendor: []
    });
    const [selectedFilters, setSelectedFilters] = useState(() => readPersistedHitlFilters());
    const [rmpmFilters, setRmpmFilters] = useState(() =>
        buildDefaultRmpmFilters(readPersistedHitlFilters()),
    );
    const [filterDrawer, setFilterDrawer] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [tableLoading, setTableLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "warning",
    });
    const tableFetchSeqRef = useRef(0);
    const [historyDraftEmails, setHistoryDraftEmails] = useState([]);
    const [historyInboxEmails, setHistoryInboxEmails] = useState([]);
    const [historySentEmails, setHistorySentEmails] = useState([]);

    function showMissingPoNumberToast() {
        setSnackBarObject({
            snackbarOpen: true,
            message: "Please fill PO number first.",
            type: "warning",
        });
    }

    function closeSnackbar() {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }




    const filtered = useMemo(() => {
        return tableData.filter((r) => {
            if (search) {
                const q = search.toLowerCase();
                return String(r.email_subject ?? r.subject ?? "").toLowerCase().includes(q) ||
                    String(r.from ?? "").toLowerCase().includes(q) ||
                    String(r.vendor ?? "").toLowerCase().includes(q);
            }
            return true;
        });
    }, [tableData, search]);

    /** Signature matches `MultiSelectWithSearchInput`: (label, values). */
    const handleMultiSelectChange = useCallback((label, values) => {
        const filter = FILTER_BY_LABEL[label];
        if (!filter) return;
        setSelectedFilters((prev) => ({ ...prev, [filter.key]: values }));
        setCurrentPage(1);
    }, []);

    const handleResetFilters = useCallback(() => {
        clearPersistedSelectedFilters(HITL_SELECTED_FILTERS_STORAGE_KEY);
        setSelectedFilters(EMPTY_SELECTED_FILTERS);
        savePersistedSelectedFilters(HITL_SELECTED_FILTERS_STORAGE_KEY, EMPTY_SELECTED_FILTERS);
        setRmpmFilters(buildDefaultRmpmFilters(EMPTY_SELECTED_FILTERS));
        setSearch("");
        setSortConfig({ key: null, direction: "asc" });
        setCurrentPage(1);
    }, []);

    const closeMoreFiltersDrawer = useCallback(() => setFilterDrawer(false), []);

    const handleFiltersSaved = useCallback((updatedFilters) => {
        setRmpmFilters(updatedFilters);
        setSelectedFilters((prev) => {
            const next = { ...prev };
            updatedFilters.forEach((f) => {
                const key = FILTER_BY_LABEL[f.label]?.key;
                if (key) next[key] = f.selectedData || [];
            });
            return next;
        });
        setCurrentPage(1);
    }, []);


    /** Drawer keys options by label; the toolbar only renders starred filters. */
    const rmpmFiltersOption = useMemo(
        () =>
            ALL_FILTERS.reduce((acc, f) => ({ ...acc, [f.label]: filterOptions[f.key] || [] }), {}),
        [filterOptions],
    );

    const favouriteToolbarFilters = useMemo(
        () =>
            [...rmpmFilters]
                .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
                .slice(0, VISIBLE_FILTER_LIMIT)
                .map((filter) => FILTER_BY_LABEL[filter.label])
                .filter(Boolean),
        [rmpmFilters],
    );

    const effectiveFilters = useMemo(() => {
        if (!Array.isArray(selectedFilters.dispatch_status) || selectedFilters.dispatch_status.length === 0) {
            return selectedFilters;
        }
        return {
            ...selectedFilters,
            dispatch_status: normalizeDispatchStatusFilterApiValues(selectedFilters.dispatch_status),
        };
    }, [selectedFilters]);

    // Persist filter selections across page refresh (same pattern as Monitor Today / TnT Linkage).
    useEffect(() => {
        savePersistedSelectedFilters(HITL_SELECTED_FILTERS_STORAGE_KEY, selectedFilters);
        setRmpmFilters((prev) =>
            prev.map((f) => {
                const key = FILTER_BY_LABEL[f.label]?.key;
                if (!key) return f;
                return {
                    ...f,
                    selectedData: [...(selectedFilters[key] || [])],
                };
            }),
        );
    }, [selectedFilters]);
    const fetchTableData = async ({ page, pageSize, filters, sort, requestId } = {}) => {
        try {
            const activeSort =
                sort ??
                (sortConfig.key
                    ? [{ key: sortConfig.key, direction: sortConfig.direction }]
                    : undefined);
            const data = await hitlEmailTableData({
                page: page ?? currentPage,
                pageSize: pageSize ?? itemsPerPage,
                filters: filters ?? effectiveFilters,
                sort: activeSort,
            });
            if (requestId != null && requestId !== tableFetchSeqRef.current) return;
            if (data) {
                setTableData(
                    Array.isArray(data.items) ? data.items.map((row) => normalizeDispatchRow(row)) : [],
                );
                setTotalItems(data.total);
                setTotalPages(data.totalPages);
                setItemsPerPage(data.pageSize);
                setCurrentPage(data.page);
            }
        } catch (error) {
            console.error("Failed to fetch table data:", error);
        }
    };


    useEffect(() => {
        let cancelled = false;
        const requestId = tableFetchSeqRef.current + 1;
        tableFetchSeqRef.current = requestId;
        (async () => {
            setTableLoading(true);
            try {
                await fetchTableData({
                    page: currentPage,
                    pageSize: itemsPerPage,
                    filters: effectiveFilters,
                    requestId,
                });
            } finally {
                if (!cancelled) setTableLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, effectiveFilters, sortConfig]);


    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await hitlGetFilters(effectiveFilters);
                if (!cancelled && data) setFilterOptions((prev) => ({ ...prev, ...data }));
            } catch (error) {
                console.error("Failed to fetch filters:", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [effectiveFilters]);

    function openExpand(r) {
        const recordKey = getRecordExpansionKey(r);
        if (!recordKey) return;
        if (expandedId === recordKey) {
            setExpandedId(null);
            return;
        }
        setExpandedId(recordKey);
        const normalizedRows = Array.isArray(r.rows) && r.rows.length > 0
            ? r.rows.map((row, index) => normalizeExpandLineItem(r, row, index))
            : [normalizeExpandLineItem(r, r, 0)];
        setLineItems(normalizedRows);
        setSelectedLines(new Set());
    }

    async function saveExpand({ confirmed = false } = {}) {
        if (!expandedId || selectedLines.size === 0) return;

        const expandedRecord = tableData.find((item) => getRecordExpansionKey(item) === expandedId);
        const editColumns = getHitlEditColumnsByIntent(expandedRecord?.email_intent ?? expandedRecord?.emailIntent);
        const hasMissingFlaggedFields = lineItems.some((item, index) => {
            if (!selectedLines.has(getLineRowKey(item, index))) return false;
            return editColumns.some((column) =>
                isFieldFlagged(expandedRecord, column.key, item) &&
                !String(resolveLineFieldValue(item, column.key, expandedRecord) ?? "").trim(),
            );
        });
        if (hasMissingFlaggedFields) return;
        const loggedInUserId =
            activeAccount?.username ??
            activeAccount?.idTokenClaims?.preferred_username ??
            activeAccount?.localAccountId ??
            "";
        const selectedPayload = lineItems
            .filter((item, index) => selectedLines.has(getLineRowKey(item, index)))
            .map((item) => {
                const updatedFields = editColumns
                    .map((column) => column.key)
                    .filter((field) => {
                        const before = resolveLineFieldValue(item.__original ?? {}, field);
                        const after = resolveLineFieldValue(item, field);
                        return String(before ?? "").trim() !== String(after ?? "").trim();
                    });
                const { __original, __originalRaw, ...updatedLineItem } = item;
                const payloadRow = { ...__originalRaw };

                updatedFields.forEach((field) => {
                    const payloadField = HITL_EDIT_FIELD_TO_PAYLOAD_FIELD[field] ?? field;
                    payloadRow[payloadField] = resolveLineFieldValue(updatedLineItem, field);
                });

                const payloadUpdatedFields = [
                    ...new Set(updatedFields.map((field) => HITL_EDIT_FIELD_TO_PAYLOAD_FIELD[field] ?? field)),
                ];

                return {
                    ...payloadRow,
                    updated_by: loggedInUserId,
                    updated_fields: payloadUpdatedFields,
                    __send_full_row: true,
                };
            });

        if (!confirmed) {
            setSaveConfirmationOpen(true);
            return;
        }

        try {
            const response = await saveHitlRows(selectedPayload);
            const validationStatus = String(response?.validation_status ?? "").trim().toLowerCase();
            const isValidationPassed =
                validationStatus === "passed" ||
                validationStatus === "validation passed" ||
                validationStatus === "validation successful" ||
                validationStatus === "successful" ||
                validationStatus.includes("pass") ||
                validationStatus.includes("success") ||
                response?.status === "validated";

            if (!isValidationPassed) {
                setValidationResult({
                    success: false,
                    message: response?.validation_status || "The selected line items could not be validated.",
                });
                return;
            }

            const selectedRowKeys = new Set(selectedPayload.map((item, index) => getLineRowKey(item, index)));
            const updateExpandedRow = (r) => {
                const recordKey = getRecordExpansionKey(r);

                if (recordKey !== expandedId) {
                    return [r];
                }

                const remainingRows = Array.isArray(r.rows)
                    ? r.rows.filter(
                        (row, index) =>
                            !selectedRowKeys.has(getLineRowKey(row, index))
                    )
                    : [];

                if (remainingRows.length > 0) {
                    return [{ ...r, rows: remainingRows, status: "pending" }];
                }

                return [];
            };

            setTableData((prev) => prev.flatMap(updateExpandedRow));
            setValidationResult({
                success: true,
                message: `${selectedPayload.length} selected line item${selectedPayload.length === 1 ? "" : "s"} validated successfully and removed from the HITL queue.`,
            });
            setSavedId(expandedId);
            setExpandedId(null);
            setSelectedLines(new Set());
            setTimeout(() => setSavedId(null), 2500);
        } catch (error) {
            console.error("Failed to save HITL rows:", error);
            setValidationResult({
                success: false,
                message: "The selected line items could not be validated. Please try again.",
            });
        }
    }

    function updateLine(rowKey, field, value) {
        setLineItems((prev) => prev.map((item) => {
            const currentKey = getLineRowKey(item, prev.indexOf(item));
            if (currentKey !== rowKey) return item;

            const nextItem = {
                ...item,
                [field]: value,
                ...(field === "vendor" ? { supplier_name: value, vendor_name: value, vendor: value } : {}),
                ...(field === "vendor_code" ? { vendor_code: value, vendorCode: value } : {}),
                ...(field === "site" ? { unit_name: value, site_name: value, site_cluster: value, site: value } : {}),
                ...(field === "site_code" ? { site_code: value, siteCode: value, unit_code: value } : {}),
                ...(field === "material" ? { mat_name: value, material_name: value, material: value } : {}),
                ...(field === "material_code" ? { mat_code: value, material_code: value, materialCode: value } : {}),
                ...(field === "po_no" ? { po_no: value, poNumber: value, po_number: value } : {}),
                ...(field === "invoice_no" ? { invoice_no: value, invoiceNo: value, invoice_number: value } : {}),
                ...(field === "vehicle_no" ? { vehicle_no: value, vehicleNo: value } : {}),
                ...(field === "dispatch_qty" ? { dispatch_qty: value, quantity: value, qty: value } : {}),
                ...(field === "stock_qty" ? { dispatch_qty: value, stock_qty: value, stockQty: value } : {}),
                ...(field === "uom" ? { uom: value } : {}),
                ...(field === "date_of_dispatch" ? { date_of_dispatch: value, dispatchDate: value, dispatch_date: value } : {}),
            };
            return nextItem;
        }));
    }

    async function handlePoNumberChange(rowKey, value) {
        updateLine(rowKey, "po_no", value);
        const normalizedPoNumber = String(value ?? "").trim();
        if (!/^\d{10}$/.test(normalizedPoNumber)) return;

        try {
            const referenceData = await hitlGetReferenceData({ po_number: normalizedPoNumber });
            const referenceValues = {
                site_code: getReferenceValue(referenceData, ["site_codes", "site_code"]),
                site: getReferenceValue(referenceData, ["site_names", "site"]),
                vendor_code: getReferenceValue(referenceData, ["vendor_codes", "vendor_code"]),
                vendor: getReferenceValue(referenceData, ["vendor_names", "vendor"]),
            };
            setLineItems((prev) => prev.map((item) => {
                if (getLineRowKey(item, prev.indexOf(item)) !== rowKey) return item;
                return {
                    ...item,
                    ...(referenceValues.site_code ? { site_code: referenceValues.site_code, siteCode: referenceValues.site_code, unit_code: referenceValues.site_code } : {}),
                    ...(referenceValues.site ? { site: referenceValues.site, site_name: referenceValues.site, site_cluster: referenceValues.site, unit_name: referenceValues.site } : {}),
                    ...(referenceValues.vendor_code ? { vendor_code: referenceValues.vendor_code, vendorCode: referenceValues.vendor_code, supplier_code: referenceValues.vendor_code } : {}),
                    ...(referenceValues.vendor ? { vendor: referenceValues.vendor, vendor_name: referenceValues.vendor, supplier_name: referenceValues.vendor } : {}),
                };
            }));
        } catch (error) {
            console.error("Failed to fetch HITL reference data for PO number:", error);
            toast.error("Unable to populate reference data for this PO number.");
        }
    }

    function handleColumnSort(column, requestedDirection) {
        if (column?.sortable === false) return;
        const sortKey = column?.sortKey ?? column?.key;
        if (!sortKey) return;
        setSortConfig((prev) => {
            const direction = requestedDirection ?? (prev.key === sortKey && prev.direction === "asc" ? "desc" : "asc");
            return { key: sortKey, direction };
        });
        setCurrentPage(1);
    }



    function renderEditableField(row, rowKey, column, record) {
        const value = String(resolveLineFieldValue(row, column.key, record) ?? "");
        const flagged = isFieldFlagged(record, column.key, row);
        const poReason = isPoNumberReason(record, row);
        const poNumber = String(resolveLineFieldValue(row, "po_no", record) ?? "").trim();
        const referenceParams = {
            vendor_code: String(resolveLineFieldValue(row, "vendor_code", record) ?? "").trim(),
            site_code: String(resolveLineFieldValue(row, "site_code", record) ?? "").trim(),
            material_code: String(resolveLineFieldValue(row, "material_code", record) ?? "").trim(),
        };

        if (column.key === "uom") {
            return (
                <select
                    className={`ehl-field ${flagged ? "ehl-field--alert" : "ehl-field--disabled"}`}
                    value={value}
                    disabled={!flagged}
                    onChange={(event) => updateLine(rowKey, column.key, event.target.value)}
                >
                    <option value="">Select UOM</option>
                    {UOM_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            );
        }

        if (["vendor", "vendor_code", "site", "site_code"].includes(column.key)) {
            return (
                <input
                    type="text"
                    className={`ehl-field ${flagged ? "ehl-field--alert" : "ehl-field--disabled"}`}
                    value={value}
                    readOnly
                    aria-label={column.label}
                />
            );
        }

        if (column.type === "master") {
            const shouldHighlight =
                column.key === "material_code"
                    ? (poReason || flagged)
                    : flagged;

            return (
                <HitlMasterDropdown
                    value={value}
                    fieldType={column.fieldType}
                    placeholder={`Select ${column.label}`}
                    className={
                        shouldHighlight
                            ? "hitl-master-dropdown--alert"
                            : "hitl-master-dropdown--disabled"
                    }
                    poNumber={poNumber}
                    referenceParams={referenceParams}
                    onMissingPo={showMissingPoNumberToast}
                    isDisabled={
                        column.key === "material_code"
                            ? !(poReason || flagged)
                            : !flagged
                    }
                    onChange={(nextValue) =>
                        updateLine(rowKey, column.key, nextValue)
                    }
                />
            );
        }

        return (
            <input
                type={column.type || "text"}
                className={`ehl-field ${flagged ? "ehl-field--alert" : "ehl-field--disabled"}`}
                // placeholder={column.placeholder || ""}
                value={value}
                disabled={!flagged}
                maxLength={column.fieldType === "poNumber" ? 10 : 50}
                onChange={(event) => {
                    if (column.key === "po_no") {
                        handlePoNumberChange(rowKey, event.target.value);
                    } else {
                        updateLine(rowKey, column.key, event.target.value);
                    }
                }}
            />
        );
    }

    function renderInlineEditPanel(record) {
        const columns = getHitlEditColumnsByIntent(record?.email_intent ?? record?.emailIntent);
        const colWidth = `${Math.max(8, Math.floor(96 / Math.max(columns.length, 1)))}%`;
        const hasMissingFlaggedFields = lineItems.some((row, index) => {
            if (!selectedLines.has(getLineRowKey(row, index))) return false;
            return columns.some((column) =>
                isFieldFlagged(record, column.key, row) &&
                !String(resolveLineFieldValue(row, column.key, record) ?? "").trim(),
            );
        });
        const saveDisabled = selectedLines.size === 0 || hasMissingFlaggedFields;

        return (
            <tr className="ehl-expand-row">
                <td colSpan={HITL_TABLE_COLUMNS.length} className="ehl-expand-cell">
                    <div className="ehl-expand-panel">
                        <div className="ehl-section-label">
                            <SquarePen size={10} color="#3949ab" />
                            <span className="ehl-section-label-text">Editable Fields</span>
                            {record.hitl_reasons?.length > 0 && (
                                <span className="ehl-hitl-chip">{record.hitl_reasons.join(" | ")}</span>
                            )}
                        </div>

                        <div className="ehl-line-wrap">
                            <table className="ehl-line-table">
                                <colgroup>
                                    <col style={{ width: "4%" }} />
                                    {columns.map((column) => (
                                        <col key={column.key} style={{ width: colWidth }} />
                                    ))}
                                </colgroup>
                                <thead>
                                    <tr className="ehl-line-thead-row">
                                        <th className="ehl-line-th--check">
                                            <input
                                                type="checkbox"
                                                className="ehl-checkbox"
                                                checked={lineItems.length > 0 && selectedLines.size === lineItems.length}
                                                onChange={toggleAllLines}
                                            />
                                        </th>
                                        {columns.map((column) => (
                                            <th key={column.key} className="ehl-line-th">{column.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="ehl-line-tbody">
                                    {lineItems.map((row, index) => {
                                        const rowKey = getLineRowKey(row, index);
                                        const isChecked = selectedLines.has(rowKey);
                                        return (
                                            <tr key={rowKey} className={isChecked ? "ehl-line-row--checked" : ""}>
                                                <td className="ehl-line-td--check">
                                                    <input
                                                        type="checkbox"
                                                        className="ehl-checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleLine(rowKey)}
                                                    />
                                                </td>
                                                {columns.map((column) => (
                                                    <td key={column.key} className="ehl-line-td">
                                                        {renderEditableField(row, rowKey, column, record)}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="ehl-panel-actions">
                            <button type="button" className="ehl-btn-ghost" onClick={() => setExpandedId(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`ehl-btn-primary ${saveDisabled ? "ehl-btn-primary--disabled" : ""}`}
                                onClick={saveExpand}
                                disabled={saveDisabled}
                            >
                                <CheckCircle2 size={12} /> Validate &amp; Save
                            </button>
                        </div>
                    </div>
                </td>
            </tr>
        );
    }

    function toggleLine(rowKey) {
        setSelectedLines((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    }

    function toggleAllLines() {
        const allKeys = lineItems.map((item, index) => getLineRowKey(item, index));
        setSelectedLines((prev) => (prev.size === allKeys.length ? new Set() : new Set(allKeys)));
    }

    const handlePageChange = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages || 1)));
    };

    const { startItem, endItem } = useMemo(() => {
        if (totalItems === 0) {
            return {
                startItem: 0,
                endItem: 0,
            };
        }

        return {
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, totalItems),
        };
    }, [currentPage, itemsPerPage, totalItems]);

    function renderPaginationPages() {
        return Array.from(
            { length: totalPages },
            (_, i) => i + 1,
        ).map((page) => {
            const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 &&
                    page <= currentPage + 1);

            const showEllipsis =
                page === currentPage - 2 ||
                page === currentPage + 2;

            if (showPage) {
                return (
                    <button
                        key={page}
                        className={`page-btn ${currentPage === page ? "active" : ""
                            }`}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </button>
                );
            }

            if (showEllipsis) {
                return (
                    <span
                        key={page}
                        className="page-ellipsis"
                    >
                        ...
                    </span>
                );
            }

            return null;
        });
    }
    const handleItemsPerPageChange = (items) => {
        setItemsPerPage(items);
        setCurrentPage(1);
    };

    function renderPagination() {
        if (tableLoading) {
            return <PaginationSkeleton />;
        }
        return (
            <div className="pagination-wrapper">
                <div className="pagination-left">
                    <span className="pagination-text">Rows per page:</span>

                    <div className="pagination-select-wrapper">
                        <select
                            value={itemsPerPage}
                            onChange={(e) =>
                                handleItemsPerPageChange(Number(e.target.value))
                            }
                            className="pagination-select"
                        >
                            {[5, 10, 20, 50].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>

                        <ChevronDown
                            size={10}
                            className="pagination-select-icon"
                        />
                    </div>

                    <span className="pagination-text">
                        {startItem}-{endItem} of {totalItems}
                    </span>
                </div>

                <div className="pagination-right">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="page-nav-btn"
                    >
                        <ChevronLeft
                            size={14}
                            className="text-gray-600"
                        />
                    </button>

                    <div className="page-numbers">
                        {renderPaginationPages()}
                    </div>

                    <button
                        disabled={
                            totalPages === 0 ||
                            currentPage >= totalPages
                        }
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="page-nav-btn"
                    >
                        <ChevronRight
                            size={14}
                            className="text-gray-600"
                        />
                    </button>
                </div>
            </div>
        );
    }

    const getHitlReasons = (record) => {
        if (Array.isArray(record.hitl_reasons)) {
            return record.hitl_reasons;
        }

        if (Array.isArray(record.hitlReasons)) {
            return record.hitlReasons;
        }

        return [];
    };

    const handleViewEmail = (record) => {
        setEmailCenterRecord(record);
        setEmailModalOpen(true);
        fetchEmailHistory(record.email_message_id ?? "");
    };

    const renderStatus = (record, wasSaved) => {
        if (wasSaved) {
            return (
                <span className="ehl-saved">
                    <CheckCircle2 size={11} aria-hidden="true" />
                    Saved
                </span>
            );
        }

        return <StatusBadge status={record.status} />;
    };

    const renderAction = (record, wasSaved) => {
        const isCompleted = record.status === "validated" || wasSaved;

        if (isCompleted) {
            return (
                <CheckCircle2
                    size={15}
                    className="ehl-done-icon"
                    aria-label="Completed"
                />
            );
        }

        return (
            <button
                type="button"
                className="ehl-icon-btn ehl-icon-btn--edit"
                title="Edit"
                aria-label={`Edit ${record.email_subject ?? "record"}`}
                onClick={() => openExpand(record)}
            >
                <SquarePen size={13} aria-hidden="true" />
            </button>
        );
    };

    const renderHitlReasons = (hitlReasons) => {
        if (hitlReasons.length === 0) {
            return (
                <span className="ehl-cell-dash" aria-label="No HITL reason">
                    —
                </span>
            );
        }

        return <span className="ehl-hitl-text">{hitlReasons.join(", ")}</span>;
    };

    const renderTableRecord = (record) => {
        const recordKey = getRecordExpansionKey(record);
        const isExpanded = expandedId === recordKey;
        const wasSaved = savedId === recordKey;
        const emailIntent = record.email_intent ?? record.emailIntent ?? "";
        const hitlReasons = getHitlReasons(record);
        const intentClass =
            INTENT_CLASS[emailIntent.toLowerCase()] ?? INTENT_CLASS.transporter;
        const reactKey = recordKey ?? record.email_message_id ?? record.id;

        return (
            <React.Fragment key={reactKey}>
                <tr className={isExpanded ? "ehl-row ehl-row--expanded" : "ehl-row"}>
                    {/* Email Intent */}
                    <Cell>
                        <span className={`ehl-intent ${intentClass}`}>
                            <span className="ehl-intent-dot" aria-hidden="true" />
                            {emailIntent}
                        </span>
                    </Cell>

                    {/* Subject */}
                    <Cell>
                        <p className="ehl-subject" title={record.email_subject}>
                            {record.email_subject}
                        </p>
                    </Cell>

                    {/* From */}
                    <Cell>
                        <span className="ehl-cell-muted" title={record.from}>
                            {record.from}
                        </span>
                    </Cell>

                    {/* To */}
                    <Cell>
                        <span className="ehl-cell-muted" title={record.to}>
                            {record.to}
                        </span>
                    </Cell>

                    {/* Vendor */}
                    <Cell>
                        <span className="ehl-cell-strong" title={record.vendor}>
                            {record.vendor}
                        </span>
                    </Cell>

                    {/* Site */}
                    <Cell>
                        <span className="ehl-cell-plain">{record.site}</span>
                    </Cell>

                    {/* Material Group */}
                    <Cell>
                        <span className="ehl-cell-muted">{record.material_group}</span>
                    </Cell>

                    {/* HITL Reason */}
                    <Cell>{renderHitlReasons(hitlReasons)}</Cell>

                    {/* Status */}
                    <Cell>{renderStatus(record, wasSaved)}</Cell>

                    {/* Email */}
                    <Cell className="ehl-td--center">
                        <button
                            type="button"
                            className="ehl-icon-btn"
                            title="View & Reply"
                            aria-label={`View and reply to ${record.email_subject ?? "email"}`}
                            onClick={() => handleViewEmail(record)}
                        >
                            <Mail size={13} aria-hidden="true" />
                        </button>
                    </Cell>

                    {/* Action */}
                    <Cell className="ehl-td--center">
                        {renderAction(record, wasSaved)}
                    </Cell>
                </tr>

                {/* Inline edit panel */}
                {isExpanded && renderInlineEditPanel(record)}
            </React.Fragment>
        );
    };

    const renderTableBody = () => {
        if (tableLoading) {
            return (
                <TableBodySkeletonRows
                    rows={10}
                    columns={HITL_TABLE_COLUMNS.length}
                />
            );
        }

        if (filtered.length === 0) {
            return (
                <tr>
                    <td colSpan={HITL_TABLE_COLUMNS.length}>
                        <div className="ehl-empty">
                            <Mail
                                size={28}
                                className="ehl-empty-icon"
                                aria-hidden="true"
                            />
                            <p className="ehl-empty-title">No records found</p>
                            <p className="ehl-empty-sub">
                                Try adjusting the search or filter
                            </p>
                        </div>
                    </td>
                </tr>
            );
        }

        return <>{filtered.map(renderTableRecord)}</>;
    };


    const fetchEmailHistory = async (emailId) => {
        console.log("i am called")
        try {
            const data = await emailHITLGetEmailHistory(emailId);
            if (data) {
                setHistoryDraftEmails(Array.isArray(data.drafts) ? data.drafts : []);
                setHistoryInboxEmails(Array.isArray(data.inbox) ? data.inbox : []);
                setHistorySentEmails(Array.isArray(data.sent_mails) ? data.sent_mails : []);
            }
            return data;
        } catch (error) {
            console.error("Failed to fetch email history:", error);
            return null;
        }

    };

    const refreshEmailHistoryForModal = useCallback(async () => {
        const emailId = emailCenterRecord?.email_message_id ?? "";
        if (!emailId) return;
        await fetchEmailHistory(emailId);
    }, [emailCenterRecord?.email_message_id]);

    return (
        <div className="">
            <SnackBarMessage
                snackbarOpen={snackBarObject.snackbarOpen}
                snackbarMessage={snackBarObject.message}
                type={snackBarObject.type}
                setSnackbarOpen={closeSnackbar}
            />
            <HitlValidationResultModal result={validationResult} onClose={() => setValidationResult(null)} />
            <HitlSaveConfirmationModal
                open={saveConfirmationOpen}
                onCancel={() => setSaveConfirmationOpen(false)}
                onConfirm={() => {
                    setSaveConfirmationOpen(false);
                    saveExpand({ confirmed: true });
                }}
            />

            {/* Filter / search bar */}
            <div
                style={{ width: "100%", display: "flex", alignItems: "end", flexWrap: "wrap" }}
            >
                {favouriteToolbarFilters.map((f) => (
                    <ListItem key={f.key} className={styles.listCustomClass}>
                        <MultiSelectWithSearchInput
                            label={f.label}
                            options={filterOptions[f.key] || []}
                            filterValues={selectedFilters[f.key] || []}
                            handleFilter={handleMultiSelectChange}
                            disabled={false}
                            type={alertType}
                        />
                    </ListItem>
                ))}

                <div className="ehl-toolbar-actions">
                    {/* <div className="ehl-search">
                        <Search size={13} className="ehl-search-icon" />
                        <input
                            type="text"
                            className="ehl-search-input"
                            placeholder="Search emails..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div> */}
                    <button
                        type="button"
                        className="reset-filter-button"
                        onClick={handleResetFilters}
                        title="Reset filters"
                        aria-label="Reset filters"
                    >
                        <img
                            src={resetBtn}
                            alt=""
                            aria-hidden="true"
                        />
                    </button>
                    {/* <OverflowMenu menuItems={overflowMenuItems} /> */}
                    <span className="ehl-record-count">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
                </div>
            </div>

            {emailCenterRecord && (
                <DraftHITLEmail
                    emailModalOpen={emailModalOpen}
                    setEmailModalOpen={setEmailModalOpen}
                    historyDraftEmails={historyDraftEmails}
                    historyInboxEmails={historyInboxEmails}
                    historySentEmails={historySentEmails}
                    emailListTab={emailListTab}
                    setEmailListTab={setEmailListTab}
                    selectedPO={{ rowId: emailCenterRecord.id, ...emailCenterRecord }}
                    onRefreshEmailHistory={refreshEmailHistoryForModal}
                    onRemoveLocalDraftEmail={() => { }}
                    onActionError={() => { }}
                    emailHistoryLoading={false}
                    autoComposeIfNoDrafts={false}
                    isReplyNowCompose={false}
                    onReplyNowAutoComposeComplete={() => { }}
                    selectedDispatchRows={[]}
                    record={emailCenterRecord}
                />
            )}

            {/* Table */}
            <div style={{ paddingTop: '12px' }}>
                <div className="ehl-table-card">
                    <div className="ehl-table-scroll">
                        <table className="ehl-table">
                            <colgroup>
                                <col style={{ width: "110px" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "13%" }} />
                                <col style={{ width: "13%" }} />
                                <col style={{ width: "72px" }} />
                                <col style={{ width: "60px" }} />
                                <col style={{ width: "110px" }} />
                                <col style={{ width: "16%" }} />
                                <col style={{ width: "88px" }} />
                                <col style={{ width: "52px" }} />
                                <col style={{ width: "72px" }} />
                            </colgroup>
                            <thead>
                                <tr className="ehl-thead-row">
                                    {HITL_TABLE_COLUMNS.map((column) => {
                                        const sortable = column.sortable !== false;
                                        const sortKey = column.sortKey ?? column.key;
                                        const active = sortConfig.key === sortKey;
                                        let ariaSort;

                                        if (!sortable) {
                                            ariaSort = undefined;
                                        } else if (!active) {
                                            ariaSort = "none";
                                        } else if (sortConfig.direction === "asc") {
                                            ariaSort = "ascending";
                                        } else {
                                            ariaSort = "descending";
                                        }
                                        return (
                                            <th
                                                key={column.key}
                                                className={`ehl-th ${column.center ? "ehl-th--center" : ""} ${sortable ? "email-triage-th-sortable" : ""}`}
                                                onClick={() => sortable && handleColumnSort(column)}
                                                scope="col"
                                                aria-sort={ariaSort}
                                            >
                                                <span className="email-triage-th-inner">
                                                    <span>{column.label}</span>
                                                    {sortable ? (
                                                        <ColumnSortIcon
                                                            direction={active ? sortConfig.direction : null}
                                                            onSort={(direction) => handleColumnSort(column, direction)}
                                                        />
                                                    ) : null}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="ehl-tbody">
                                {renderTableBody()}
                            </tbody>
                        </table>
                    </div>
                </div>
                {renderPagination()}
                <Toaster />
            </div>

            {filterDrawer && (
                <MoreFilters
                    anchor="right"
                    filterDrawer={filterDrawer}
                    handleFilterDrawer={closeMoreFiltersDrawer}
                    rmpmFilters={rmpmFilters}
                    setRmpmFilters={setRmpmFilters}
                    options={rmpmFiltersOption}
                    onFiltersSaved={handleFiltersSaved}
                    limit={VISIBLE_FILTER_LIMIT}
                />
            )}
        </div>
    );
}

EmailValidationTab.propTypes = {
    alertType: PropTypes.string,
};

export default EmailValidationTab;
